import Decimal from "decimal.js";

import * as FREQUENCY from "../constants/frequencies";
import { HOA_PROPERTY_TYPES } from "../constants/property_type";
import { YES } from "../constants/yes_no";
import { REFINANCE } from "../constants/document_types";
import { selected_balance_pay_offs_total } from "./debt_info";
import { LENDER_FEE } from "../constants/fixed_values";
import { ESCROW_IN_PAYMENT, NO_MORTGAGE } from "../constants/escrow_included";

/**
 * Selling Property - 1st Current Monthly Payment (Principal & Interest)
 */
export function first_current_monthly_payment(property_to_be_sold) {
  return new Decimal(
    property_to_be_sold.first_current_monthly_payment_principal
  ).add(property_to_be_sold.first_current_monthly_payment_interest);
}

/**
 * Calculation for the total of loans
 */
export function total_of_loans(
  documentType,
  property_to_be_sold,
  accountSummaries,
  residences,
  loans,
  summary
) {
  return new Decimal(property_to_be_sold.first_loan_balance)
    .add(property_to_be_sold.second_loan_balance)
    .add(
      new Decimal(property_to_be_sold.miscellaneous_housing_payment).gt(0)
        ? property_to_be_sold.third_loan_balance
        : 0
    )
    .add(
      documentType === REFINANCE
        ? selected_balance_pay_offs_total(
          loans,
          summary,
          accountSummaries,
          residences
        )
        : 0
    );
}

/**
 * Convert an amount + frequency (Monthly / Quarterly / Annually) to monthly.
 */
export function amount_frequency_monthly(amount, frequency) {
  switch (frequency) {
    case FREQUENCY.ANNUALLY:
      return new Decimal(amount || 0).div(12);
    case FREQUENCY.QUARTERLY:
      return new Decimal(amount || 0).div(3);
    default:
      return new Decimal(amount || 0);
  }
}

/**
 * Selling Property - present-housing escrow (property tax, home insurance,
 * HOA 1/2, Mello Roos) as a single monthly amount.
 */
export function present_housing_escrow_monthly(property_to_be_sold) {
  return amount_frequency_monthly(
    property_to_be_sold.present_property_tax,
    property_to_be_sold.present_property_tax_frequency
  )
    .plus(
      amount_frequency_monthly(
        property_to_be_sold.present_home_insurance,
        property_to_be_sold.present_home_insurance_frequency
      )
    )
    .plus(
      amount_frequency_monthly(
        property_to_be_sold.present_hoa1,
        property_to_be_sold.present_hoa1_frequency
      )
    )
    .plus(
      amount_frequency_monthly(
        property_to_be_sold.present_hoa2,
        property_to_be_sold.present_hoa2_frequency
      )
    )
    .plus(
      amount_frequency_monthly(
        property_to_be_sold.present_mello_roos,
        property_to_be_sold.present_mello_roos_frequency
      )
    )
    .plus(
      amount_frequency_monthly(
        property_to_be_sold.present_flood_insurance,
        property_to_be_sold.present_flood_insurance_frequency
      )
    );
}

/**
 * A home Borrower 1 is KEEPING (Status Retained), broken into monthly pieces
 * the way the 1003 / MISMO 3.4 split them:
 *   - the mortgage payment(s) the servicer collects (the credit-report payment),
 *     with existing mortgage insurance in the 1st and a 3rd lien in the 2nd;
 *   - the costs NOT in that payment (taxes, insurance + flood, HOA, Mello Roos).
 * "Impounded" = taxes and insurance are already inside the payment, so they are
 * not added again. "No mortgage" = free and clear: no payment, no balance.
 */
export function kept_home_breakdown(pts) {
  const p = pts || {};
  const noMortgage = p.payment_includes_escrow === NO_MORTGAGE;
  const impounded = p.payment_includes_escrow === ESCROW_IN_PAYMENT;
  const zero = new Decimal(0);
  return {
    first_mortgage: noMortgage
      ? zero
      : new Decimal(p.first_current_monthly_payment_principal || 0)
          .add(p.first_current_monthly_payment_interest || 0)
          .add(p.existing_mortgage_insurance || 0),
    second_mortgage: noMortgage
      ? zero
      : new Decimal(p.second_current_monthly_payment || 0).add(
          p.miscellaneous_housing_payment || 0
        ),
    taxes: impounded
      ? zero
      : amount_frequency_monthly(p.present_property_tax, p.present_property_tax_frequency),
    insurance: impounded
      ? zero
      : amount_frequency_monthly(
          p.present_home_insurance,
          p.present_home_insurance_frequency
        ).add(
          amount_frequency_monthly(
            p.present_flood_insurance,
            p.present_flood_insurance_frequency
          )
        ),
    hoa1: amount_frequency_monthly(p.present_hoa1, p.present_hoa1_frequency),
    hoa2: amount_frequency_monthly(p.present_hoa2, p.present_hoa2_frequency),
    mello_roos: amount_frequency_monthly(
      p.present_mello_roos,
      p.present_mello_roos_frequency
    ),
    principal_balance: noMortgage
      ? zero
      : new Decimal(p.first_loan_balance || 0)
          .add(p.second_loan_balance || 0)
          .add(p.third_loan_balance || 0)
  };
}

/** Full monthly cost of a kept home: payment(s) + costs not in the payment. */
export function kept_home_monthly_total(pts) {
  const b = kept_home_breakdown(pts);
  return b.first_mortgage
    .add(b.second_mortgage)
    .add(b.taxes)
    .add(b.insurance)
    .add(b.hoa1)
    .add(b.hoa2)
    .add(b.mello_roos);
}

/**
 * Calculation for the total of payments
 */
export function total_of_payments(property_to_be_sold) {
  return new Decimal(first_current_monthly_payment(property_to_be_sold))
    .plus(property_to_be_sold.second_current_monthly_payment)
    .plus(property_to_be_sold.miscellaneous_housing_payment)
    .plus(property_to_be_sold.existing_mortgage_insurance)
    .plus(present_housing_escrow_monthly(property_to_be_sold));
}

/**
 * Calculation for Home Owners Insurance (Monthly)
 */
export function home_owners_insurance_monthly(documentType, property) {
  const value = property.home_owners_insurance || "0";
  const freq = property.home_owners_insurance_frequency;
  if (documentType === REFINANCE) {
    // Refinance (unchanged): empty/monthly => monthly figure.
    switch (freq) {
      case FREQUENCY.ANNUALLY:
        return new Decimal(value).div(12);
      case FREQUENCY.QUARTERLY:
        return new Decimal(value).div(3);
      default:
        return new Decimal(value);
    }
  }
  // Purchase: the LO can now pick Monthly / Quarterly / Annually. Empty defaults
  // to Annually (value/12) so every existing purchase file is unchanged.
  switch (freq) {
    case FREQUENCY.MONTHLY:
      return new Decimal(value);
    case FREQUENCY.QUARTERLY:
      return new Decimal(value).div(3);
    default: // Annually or empty
      return new Decimal(value).div(12);
  }
}

/**
 * Flood Insurance as a monthly amount. Flood is classified as insurance (same as
 * HOI): a separate line item that impounds and rolls into PITIA. Mirrors
 * home_owners_insurance_monthly — refinance honors the frequency; purchase assumes
 * an annual amount (/12). Source page: New Property (purchase) / Subject Property
 * (refinance), field property.flood_insurance.
 */
export function flood_insurance_monthly(documentType, property) {
  const value = property.flood_insurance || "0";
  const freq = property.flood_insurance_frequency;
  if (documentType === REFINANCE) {
    switch (freq) {
      case FREQUENCY.ANNUALLY:
        return new Decimal(value).div(12);
      case FREQUENCY.QUARTERLY:
        return new Decimal(value).div(3);
      default:
        return new Decimal(value);
    }
  }
  // Purchase: LO picks Monthly / Quarterly / Annually; empty => Annually (/12).
  switch (freq) {
    case FREQUENCY.MONTHLY:
      return new Decimal(value);
    case FREQUENCY.QUARTERLY:
      return new Decimal(value).div(3);
    default: // Annually or empty
      return new Decimal(value).div(12);
  }
}

/**
 * Helper - If the property is of type that includes HOA
 */
function is_hoa_property(property) {
  return HOA_PROPERTY_TYPES.includes(property.property_type);
}

/**
 * Calculation for HOA Dues 1
 */
export function hoa_dues_1(property, loan) {
  if (
    !is_hoa_property(property) &&
    (!loan || !HOA_PROPERTY_TYPES.includes(loan.property_type))
  ) {
    return new Decimal(0);
  }

  let frequency = property.hoa1_frequency;
  let value = property.hoa1;
  if (loan && HOA_PROPERTY_TYPES.includes(loan.property_type)) {
    frequency = loan.hoa1_frequency;
    value = loan.hoa1;
  }
  switch (frequency) {
    case FREQUENCY.ANNUALLY:
      return new Decimal(value).div(12);
    case FREQUENCY.QUARTERLY:
      return new Decimal(value).div(3);
    default:
      return new Decimal(value);
  }
}

/**
 * Calculation for HOA Dues 2
 */
export function hoa_dues_2(property, loan) {
  if (
    !is_hoa_property(property) &&
    (!loan || !HOA_PROPERTY_TYPES.includes(loan.property_type))
  ) {
    return new Decimal(0);
  }

  let frequency = property.hoa2_frequency;
  let value = property.hoa2;
  if (loan && HOA_PROPERTY_TYPES.includes(loan.property_type)) {
    frequency = loan.hoa2_frequency;
    value = loan.hoa2;
  }
  switch (frequency) {
    case FREQUENCY.ANNUALLY:
      return new Decimal(value).div(12);
    case FREQUENCY.QUARTERLY:
      return new Decimal(value).div(3);
    default:
      return new Decimal(value);
  }
}

/**
 * Calculation for Mello Roos
 */
export function mello_roos(property, loan) {
  if (
    (!is_hoa_property(property) || property.mello_roos !== YES) &&
    (!loan || !HOA_PROPERTY_TYPES.includes(loan.property_type))
  ) {
    return new Decimal(0);
  }

  let frequency = property.mello_roos_frequency;
  let value = property.mello_roos_amount;
  if (loan && HOA_PROPERTY_TYPES.includes(loan.property_type)) {
    frequency = loan.mello_roos_frequency;
    value = loan.mello_roos_amount;
  }
  switch (frequency) {
    case FREQUENCY.ANNUALLY:
      return new Decimal(value).div(12);
    case FREQUENCY.QUARTERLY:
      return new Decimal(value).div(3);
    default:
      return new Decimal(value);
  }
}

/**
 * Total HOA per month.
 */
export function total_hoas_monthly(property, loan) {
  return hoa_dues_1(property, loan).add(hoa_dues_2(property, loan));
}

/**
 * Gets the renderable value of reported on 1040
 * @param {*} property
 */
export function reported_on_1040(property) {
  switch (property.reported_on_1040) {
    case "1":
      return "Yes Rpt";
    case "2":
      return "Not Rpt";
    default:
      throw Error(
        `Invalid reported_on_1040 value: ${property.reported_on_1040}`
      );
  }
}

/**
 * Summary Sheet - Refinance - General Info Section.
 */
export function first_and_second_current_monthly_payment(property_to_be_sold) {
  return first_current_monthly_payment(property_to_be_sold).add(
    property_to_be_sold.second_current_monthly_payment
  );
}

/**
 * Summary Sheet - Refinance - General Info Section.
 */
export function first_and_second_current_monthly_payment_with_lender_fee(
  property_to_be_sold
) {
  const secondPayment = new Decimal(
    property_to_be_sold.second_current_monthly_payment
  );
  return new Decimal(0)
    .add(
      first_current_monthly_payment(property_to_be_sold).gt(0)
        ? first_current_monthly_payment(property_to_be_sold).add(LENDER_FEE)
        : 0
    )
    .add(secondPayment.gt(0) ? secondPayment.add(LENDER_FEE) : 0);
}

/**
 * Summary Sheet - Refinance - FHA / PMI Mo Pmt.
 */
export function fha_or_pmi_mo_pmt(property_to_be_sold) {
  return new Decimal(property_to_be_sold.existing_mortgage_insurance).add(
    property_to_be_sold.miscellaneous_housing_payment
  );
}

/**
 * Summary Sheet - Refinance - Total PI MI Fees
 */
export function total_pi_mi_fees(property_to_be_sold) {
  return first_and_second_current_monthly_payment(property_to_be_sold).add(
    fha_or_pmi_mo_pmt(property_to_be_sold)
  );
}

/**
 * Summary Sheet - Refinance - FHA / PMI Pay Off
 */
export function fha_or_pmi_pay_off(property_to_be_sold) {
  const miscellaneousHousingPayment = new Decimal(
    property_to_be_sold.miscellaneous_housing_payment
  );
  return miscellaneousHousingPayment.gt(0)
    ? miscellaneousHousingPayment
    : new Decimal(property_to_be_sold.existing_mortgage_insurance);
}

/**
 * Summary Sheet - Refinance - Total PI & MI
 */
export function total_pi_and_mi(property_to_be_sold) {
  return first_and_second_current_monthly_payment_with_lender_fee(
    property_to_be_sold
  ).add(fha_or_pmi_pay_off(property_to_be_sold));
}
